import React, { useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Box,
  Chip,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import moment from "moment";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import PersonIcon from "@mui/icons-material/Person";

const MyPatients = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [tabValue, setTabValue] = useState(0);

  const {
    data: patientsData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["nurse-patients"],
    queryFn: () =>
      axios
        .get(`${process.env.REACT_APP_API_URL}/nurse-patients/`, {
          headers: { Authorization: `Token ${token}` },
        })
        .then((res) => res.data),
    enabled: !!token,
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error?.message || t("nurse.errorLoadingPatients", "Failed to load patients.")}
      </Alert>
    );
  }

  const ipdPatients = patientsData?.ipd_patients || [];
  const icuPatients = patientsData?.icu_patients || [];
  const allPatients = [...ipdPatients, ...icuPatients];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h5" gutterBottom>
            {t("nurse.myPatients", "My Patients")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("nurse.myPatientsDescription", "Patients assigned to you in IPD and ICU")}
          </Typography>

          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label={t("nurse.allPatients", `All (${allPatients.length})`)} />
            <Tab label={t("nurse.ipdPatients", `IPD (${ipdPatients.length})`)} />
            <Tab label={t("nurse.icuPatients", `ICU (${icuPatients.length})`)} />
          </Tabs>

          {tabValue === 0 && (
            <Grid container spacing={2}>
              {allPatients.length === 0 ? (
                <Grid item xs={12}>
                  <Alert severity="info">
                    {t("nurse.noPatientsAssigned", "No patients assigned to you.")}
                  </Alert>
                </Grid>
              ) : (
                allPatients.map((patient) => (
                  <Grid item xs={12} md={6} lg={4} key={`${patient.type}-${patient.id}`}>
                    <PatientCard patient={patient} t={t} />
                  </Grid>
                ))
              )}
            </Grid>
          )}

          {tabValue === 1 && (
            <Grid container spacing={2}>
              {ipdPatients.length === 0 ? (
                <Grid item xs={12}>
                  <Alert severity="info">
                    {t("nurse.noIpdPatients", "No IPD patients assigned to you.")}
                  </Alert>
                </Grid>
              ) : (
                ipdPatients.map((patient) => (
                  <Grid item xs={12} md={6} lg={4} key={`ipd-${patient.id}`}>
                    <PatientCard patient={patient} t={t} />
                  </Grid>
                ))
              )}
            </Grid>
          )}

          {tabValue === 2 && (
            <Grid container spacing={2}>
              {icuPatients.length === 0 ? (
                <Grid item xs={12}>
                  <Alert severity="info">
                    {t("nurse.noIcuPatients", "No ICU patients assigned to you.")}
                  </Alert>
                </Grid>
              ) : (
                icuPatients.map((patient) => (
                  <Grid item xs={12} md={6} lg={4} key={`icu-${patient.id}`}>
                    <PatientCard patient={patient} t={t} />
                  </Grid>
                ))
              )}
            </Grid>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

const PatientCard = ({ patient, t }) => {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocalHospitalIcon color={patient.type === "ICU" ? "error" : "primary"} />
            <Typography variant="h6">{patient.patient_name}</Typography>
          </Box>
          <Chip
            label={patient.type}
            color={patient.type === "ICU" ? "error" : "primary"}
            size="small"
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              <strong>{t("nurse.bed", "Bed")}:</strong> {patient.bed}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            <strong>{t("nurse.department", "Department")}:</strong> {patient.department}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            <strong>{t("nurse.admissionDate", "Admission Date")}:</strong>{" "}
            {moment(patient.admission_date).format("YYYY-MM-DD")}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            <strong>{t("nurse.doctor", "Doctor")}:</strong> {patient.admitting_doctor}
          </Typography>

          {patient.shift && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>{t("nurse.shift", "Shift")}:</strong> {patient.shift}
            </Typography>
          )}

          {patient.role && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>{t("nurse.role", "Role")}:</strong> {patient.role}
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <strong>{t("nurse.diagnosis", "Diagnosis")}:</strong> {patient.admission_diagnosis}
          </Typography>
        </Box>

        <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              if (patient.type === "IPD") {
                window.location.href = `/ipd/admissions/${patient.id}`;
              } else {
                window.location.href = `/icu/admissions/${patient.id}`;
              }
            }}
          >
            {t("nurse.viewDetails", "View Details")}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MyPatients;
